import { useRef } from "react";

export const ASPECTS = [["Libre", null], ["Original", "orig"], ["1:1", 1], ["4:5", 0.8], ["3:2", 1.5], ["16:9", 16 / 9]];

// Largest centred crop for a pixel aspect ratio within a frame (normalised coords)
export function fitCrop(aspect, frameW, frameH) {
  if (!aspect) return { x: 0, y: 0, w: 1, h: 1 };
  const frameAspect = frameW / frameH;
  if (aspect > frameAspect) { const h = frameAspect / aspect; return { x: 0, y: (1 - h) / 2, w: 1, h }; }
  const w = aspect / frameAspect;
  return { x: (1 - w) / 2, y: 0, w, h: 1 };
}

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export function CropOverlay({ crop, onChange, aspect, frameW, frameH }) {
  const ref = useRef(null);
  const drag = useRef(null);

  const down = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { mode, sx: e.clientX, sy: e.clientY, start: { ...crop } };
  };
  const move = (e) => {
    if (!drag.current) return;
    const r = ref.current.getBoundingClientRect();
    const dx = (e.clientX - drag.current.sx) / r.width;
    const dy = (e.clientY - drag.current.sy) / r.height;
    const s = drag.current.start;
    if (drag.current.mode === "move") {
      onChange({ ...s, x: clamp(s.x + dx, 0, 1 - s.w), y: clamp(s.y + dy, 0, 1 - s.h) });
      return;
    }
    const [hx, hy] = drag.current.mode; // corner: -1 left/top, 1 right/bottom
    const ax = hx > 0 ? s.x : s.x + s.w;
    const ay = hy > 0 ? s.y : s.y + s.h;
    let w = clamp(hx > 0 ? s.w + dx : s.w - dx, 0.05, hx > 0 ? 1 - ax : ax);
    let h = clamp(hy > 0 ? s.h + dy : s.h - dy, 0.05, hy > 0 ? 1 - ay : ay);
    if (aspect) {
      h = (w * frameW) / (frameH * aspect);
      const maxH = hy > 0 ? 1 - ay : ay;
      if (h > maxH) { h = maxH; w = (h * frameH * aspect) / frameW; }
    }
    onChange({ x: hx > 0 ? ax : ax - w, y: hy > 0 ? ay : ay - h, w, h });
  };
  const up = () => { drag.current = null; };

  const pct = (v) => `${v * 100}%`;
  return (
    <div ref={ref} className="absolute inset-0 touch-none select-none" onPointerMove={move} onPointerUp={up} data-testid="crop-overlay">
      <div className="absolute border border-white cursor-move" onPointerDown={down("move")}
        style={{ left: pct(crop.x), top: pct(crop.y), width: pct(crop.w), height: pct(crop.h), boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)" }} data-testid="crop-box">
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(to right, transparent 33.2%, rgba(255,255,255,.35) 33.3%, transparent 33.4%, transparent 66.5%, rgba(255,255,255,.35) 66.6%, transparent 66.7%), linear-gradient(to bottom, transparent 33.2%, rgba(255,255,255,.35) 33.3%, transparent 33.4%, transparent 66.5%, rgba(255,255,255,.35) 66.6%, transparent 66.7%)" }} />
        {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([hx, hy]) => (
          <div key={`${hx}${hy}`} onPointerDown={down([hx, hy])} data-testid={`crop-handle-${hx}${hy}`}
            className="absolute w-4 h-4 bg-white rounded-sm" style={{ left: hx < 0 ? -8 : undefined, right: hx > 0 ? -8 : undefined, top: hy < 0 ? -8 : undefined, bottom: hy > 0 ? -8 : undefined, cursor: hx * hy > 0 ? "nwse-resize" : "nesw-resize" }} />
        ))}
      </div>
    </div>
  );
}
